import React, { useEffect, useState, useCallback, useRef } from "react";
import AdvertItem from "../items/vip_advert_item";
import RubricItem from "../items/RubricItem";
import { getFeaturedAdverts, getRegularAdverts } from '../../../api/getAdverts';
import { getRubrics } from '../../../api/getRubrics';
import { useTranslation } from "react-i18next";

import image1 from '../../images/vip1.png';
import siteLogo from '../../../app_icon.svg';
import Dropdown from "../../../common/components/Dropdown";
import Loading from "../../../common/components/Loading";
import { getCountries } from "../../../api/location/getCountries";
import { getRegions } from "../../../api/location/getRegions";
import { getLocalizedName } from "../../../api/location/locationUtils";

const ADVERT_CACHE_KEY = "advertSectionCache_v1";

// Single filter row with chevron
function FilterRow({ label, children, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="border-b border-gray-100 last:border-b-0">
            <button
                className="w-full flex items-center justify-between py-3 text-sm text-gray-700 hover:text-gray-900 transition-colors"
                onClick={() => setOpen(!open)}
            >
                <span>{label}</span>
                <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && <div className="pb-3">{children}</div>}
        </div>
    );
}

function AdvertSection() {
    const { t, i18n } = useTranslation();
    const [displayedAdverts, setDisplayedAdverts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const [vipBatches, setVipBatches] = useState([]);
    const [nonVipBatches, setNonVipBatches] = useState([]);
    const [hasMoreVip, setHasMoreVip] = useState(true);
    const [hasMoreNonVip, setHasMoreNonVip] = useState(true);

    const isFetchingVip = useRef(false);
    const isFetchingNonVip = useRef(false);
    const vipPageRef = useRef(1);
    const nonVipPageRef = useRef(1);
    const observerTarget = useRef(null);

    const [advertMode, setAdvertMode] = useState('regular'); // 'regular' | 'dealer'
    const [categoryFilter, setCategoryFilter] = useState({ categoryId: null, subcategoryId: null });
    const [sortOption, setSortOption] = useState('');
    const [negotiableOnly, setNegotiableOnly] = useState(false);

    const [countries, setCountries] = useState([]);
    const [regions, setRegions] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(null);

    // Price range filter state
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');

    // Rubrics
    const [rubrics, setRubrics] = useState([]);
    const [activeRubricId, setActiveRubricId] = useState(null);

    // Category / subcategory dropdown selection
    const [selectedCategoryObj, setSelectedCategoryObj] = useState(null);
    const [selectedSubcategoryObj, setSelectedSubcategoryObj] = useState(null);

    const [showAllRubrics, setShowAllRubrics] = useState(false);

    const VIP_BATCH_SIZE = 5;
    const NON_VIP_BATCH_SIZE = 5;

    useEffect(() => {
        try {
            const rawCache = sessionStorage.getItem(ADVERT_CACHE_KEY);
            if (!rawCache) return;
            const cache = JSON.parse(rawCache);
            if (!cache || !Array.isArray(cache.vipBatches) || !Array.isArray(cache.nonVipBatches)) return;
            setVipBatches(cache.vipBatches);
            setNonVipBatches(cache.nonVipBatches);
            setDisplayedAdverts(Array.isArray(cache.displayedAdverts) ? cache.displayedAdverts : []);
            setHasMoreVip(typeof cache.hasMoreVip === "boolean" ? cache.hasMoreVip : true);
            setHasMoreNonVip(typeof cache.hasMoreNonVip === "boolean" ? cache.hasMoreNonVip : true);
            vipPageRef.current = Number.isFinite(cache.vipPage) ? cache.vipPage : 1;
            nonVipPageRef.current = Number.isFinite(cache.nonVipPage) ? cache.nonVipPage : 1;
            setIsLoading(false);
        } catch (e) {
            console.error("Failed to restore vip adverts cache:", e);
        }
    }, []);

    const getLocationParams = useCallback(() => {
        try {
            const storedVip = localStorage.getItem('vipSelectedLocation');
            const storedFallback = localStorage.getItem('selectedLocation');
            const stored = storedVip || storedFallback;
            if (!stored) return {};
            const loc = JSON.parse(stored);
            const params = {};
            if (loc.country_id) params.country_id = loc.country_id;
            if (loc.region_id) params.region_id = loc.region_id;
            if (loc.district_id) params.district_id = loc.district_id;
            return params;
        } catch {
            return {};
        }
    }, []);

    const buildFilterParams = useCallback(() => {
        const params = {};
        if (categoryFilter.categoryId) params.category_id = categoryFilter.categoryId;
        if (categoryFilter.subcategoryId) params.subcategory_id = categoryFilter.subcategoryId;
        if (negotiableOnly) params.negotiable = true;
        if (advertMode === 'dealer') params.is_dealer = true;
        if (sortOption === 'date_newest') params.sort_by_date = 'desc';
        else if (sortOption === 'date_oldest') params.sort_by_date = 'asc';
        else if (sortOption === 'price_low') params.sort_by_price = 'asc';
        else if (sortOption === 'price_high') params.sort_by_price = 'desc';
        return params;
    }, [categoryFilter, negotiableOnly, sortOption, advertMode]);

    const fetchVipBatch = useCallback(async () => {
        if (isFetchingVip.current || !hasMoreVip) return;
        isFetchingVip.current = true;
        const locationParams = getLocationParams();
        const filterParams = buildFilterParams();
        try {
            const data = await getFeaturedAdverts({ page: vipPageRef.current, limit: VIP_BATCH_SIZE, ...locationParams, ...filterParams });
            if (data.items && data.items.length > 0) {
                setVipBatches((prev) => [...prev, data.items]);
                vipPageRef.current += 1;
                if (data.items.length < VIP_BATCH_SIZE) setHasMoreVip(false);
            } else {
                setHasMoreVip(false);
            }
        } catch (error) {
            console.error("Error fetching featured adverts:", error);
            setHasMoreVip(false);
        } finally {
            isFetchingVip.current = false;
        }
    }, [hasMoreVip, getLocationParams, buildFilterParams]);

    const fetchNonVipBatch = useCallback(async () => {
        if (isFetchingNonVip.current || !hasMoreNonVip) return;
        isFetchingNonVip.current = true;
        setIsLoadingMore(true);
        const locationParams = getLocationParams();
        const filterParams = buildFilterParams();
        try {
            const data = await getRegularAdverts({ page: nonVipPageRef.current, limit: NON_VIP_BATCH_SIZE, ...locationParams, ...filterParams });
            if (data.items && data.items.length > 0) {
                setNonVipBatches((prev) => [...prev, data.items]);
                nonVipPageRef.current += 1;
                if (data.items.length < NON_VIP_BATCH_SIZE) setHasMoreNonVip(false);
            } else {
                setHasMoreNonVip(false);
            }
        } catch (error) {
            console.error("Error fetching non-VIP adverts:", error);
            setHasMoreNonVip(false);
        } finally {
            setIsLoadingMore(false);
            isFetchingNonVip.current = false;
        }
    }, [hasMoreNonVip, getLocationParams, buildFilterParams]);

    const resetAndRefetch = useCallback(() => {
        sessionStorage.removeItem(ADVERT_CACHE_KEY);
        setVipBatches([]);
        setNonVipBatches([]);
        setDisplayedAdverts([]);
        setHasMoreVip(true);
        setHasMoreNonVip(true);
        vipPageRef.current = 1;
        nonVipPageRef.current = 1;
        setIsLoading(true);
    }, []);

    useEffect(() => {
        const adverts = [];
        const maxBatches = Math.max(vipBatches.length, nonVipBatches.length);
        for (let i = 0; i < maxBatches; i++) {
            if (i < vipBatches.length) vipBatches[i].forEach(a => adverts.push({ ...a, _key: `vip-${a.id}-batch${i}` }));
            if (i < nonVipBatches.length) nonVipBatches[i].forEach(a => adverts.push({ ...a, _key: `nonvip-${a.id}-batch${i}` }));
        }
        const unique = adverts.reduce((acc, cur) => {
            if (!acc.find(x => x.id === cur.id)) acc.push(cur);
            return acc;
        }, []);
        setDisplayedAdverts(unique);
    }, [vipBatches, nonVipBatches]);

    useEffect(() => {
        if (!isLoading || vipBatches.length > 0) return;
        let cancelled = false;
        (async () => {
            await fetchVipBatch();
            if (!cancelled) setIsLoading(false);
        })();
        return () => { cancelled = true; };
    }, [isLoading, vipBatches.length, fetchVipBatch]);

    useEffect(() => {
        if (!vipBatches.length && !nonVipBatches.length && !displayedAdverts.length) return;
        const cache = { vipBatches, nonVipBatches, displayedAdverts, hasMoreVip, hasMoreNonVip, vipPage: vipPageRef.current, nonVipPage: nonVipPageRef.current };
        sessionStorage.setItem(ADVERT_CACHE_KEY, JSON.stringify(cache));
    }, [vipBatches, nonVipBatches, displayedAdverts, hasMoreVip, hasMoreNonVip]);

    useEffect(() => {
        const onStorage = (e) => { if (e.key === 'selectedLocation') resetAndRefetch(); };
        const onLocationChanged = () => resetAndRefetch();
        window.addEventListener('storage', onStorage);
        window.addEventListener('agroSelectedLocationChanged', onLocationChanged);
        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('agroSelectedLocationChanged', onLocationChanged);
        };
    }, [resetAndRefetch]);

    useEffect(() => {
        if (vipBatches.length > 0 && nonVipBatches.length < vipBatches.length && !isLoading) fetchNonVipBatch();
    }, [vipBatches.length, nonVipBatches.length, isLoading, fetchNonVipBatch]);

    useEffect(() => {
        if (!hasMoreVip && hasMoreNonVip && vipBatches.length > 0 && nonVipBatches.length === vipBatches.length) fetchNonVipBatch();
    }, [hasMoreVip, hasMoreNonVip, vipBatches.length, nonVipBatches.length, fetchNonVipBatch]);

    const loadMore = useCallback(async () => {
        if (isLoadingMore || (isFetchingVip.current && isFetchingNonVip.current)) return;
        const lastVipBatch = vipBatches.length > 0 ? vipBatches[vipBatches.length - 1] : [];
        const remainingVipCount = lastVipBatch.length;
        const lastTwoAdverts = displayedAdverts.slice(-2);
        const lastTwoAreVip = lastTwoAdverts.length === 2 && lastTwoAdverts.every(ad => ad._key?.startsWith('vip-'));
        if (!hasMoreVip && remainingVipCount === 2 && lastTwoAreVip) {
            if (hasMoreNonVip) { await fetchNonVipBatch(); if (hasMoreNonVip && !isFetchingNonVip.current) await fetchNonVipBatch(); }
            return;
        }
        if (!hasMoreVip && remainingVipCount > 0 && remainingVipCount <= 2) {
            if (hasMoreNonVip) { await fetchNonVipBatch(); if (hasMoreNonVip && !isFetchingNonVip.current) await fetchNonVipBatch(); }
            return;
        }
        if (hasMoreVip) {
            await fetchVipBatch();
            if (hasMoreNonVip) await fetchNonVipBatch();
        } else if (hasMoreNonVip) {
            await fetchNonVipBatch();
            if (hasMoreNonVip && !isFetchingNonVip.current) await fetchNonVipBatch();
        }
    }, [displayedAdverts, hasMoreVip, hasMoreNonVip, vipBatches, fetchVipBatch, fetchNonVipBatch, isLoadingMore]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && (hasMoreVip || hasMoreNonVip) && !isLoadingMore && !isFetchingVip.current && !isFetchingNonVip.current) loadMore();
            },
            { threshold: 0.1 }
        );
        const currentTarget = observerTarget.current;
        if (currentTarget) observer.observe(currentTarget);
        return () => { if (currentTarget) observer.unobserve(currentTarget); };
    }, [hasMoreVip, hasMoreNonVip, isLoadingMore, loadMore]);

    useEffect(() => { resetAndRefetch(); }, [categoryFilter, sortOption, negotiableOnly, advertMode, resetAndRefetch]);

    const getCategoryName = (cat) => {
        const lang = i18n.language;
        return (lang === 'kz' ? cat.name_kz : lang === 'en' ? cat.name_en : cat.name_ru) || cat.name_ru || cat.name || '';
    };

    const getSubcategoryName = (sub) => {
        const lang = i18n.language;
        return (lang === 'kz' ? sub.name_kz : lang === 'en' ? sub.name_en : sub.name_ru) || sub.name_ru || sub.name || '';
    };

    const allCategories = rubrics.flatMap(r => r.categories || []);

    const handleCategoryDropdownSelect = (option) => {
        const cat = allCategories.find(c => getCategoryName(c) === option) || null;
        setSelectedCategoryObj(cat);
        setSelectedSubcategoryObj(null);
        setActiveRubricId(null);
        setCategoryFilter({ categoryId: cat?.category_id || null, subcategoryId: null });
    };

    const handleSubcategoryDropdownSelect = (option) => {
        const subs = selectedCategoryObj?.subcategories || [];
        const sub = subs.find(s => getSubcategoryName(s) === option) || null;
        setSelectedSubcategoryObj(sub);
        setCategoryFilter({ categoryId: selectedCategoryObj?.category_id || null, subcategoryId: sub?.subcategory_id || sub?.id || null });
    };

    const handlePriceSortChange = (option) => {
        if (option === t('priceAscSort')) setSortOption('price_low');
        else if (option === t('priceDesSort')) setSortOption('price_high');
    };

    const handleDateSortChange = (option) => {
        if (option === t('dateDesSort')) setSortOption('date_newest');
        else if (option === t('dateAscSort')) setSortOption('date_oldest');
    };

    useEffect(() => {
        getRubrics().then((data) => {
            const filtered = data.filter((r) => r.categories && r.categories.length > 0);
            setRubrics(filtered);
        });
    }, []);

    const getRubricName = (rubric) => {
        const lang = i18n.language;
        return (lang === 'kz' ? rubric.rubric_name_kz : lang === 'en' ? rubric.rubric_name_en : rubric.rubric_name_ru) || rubric.rubric_name_ru || rubric.rubric_name || '';
    };

    useEffect(() => {
        const loadCountries = async () => {
            try {
                const data = await getCountries();
                setCountries(data || []);
                if (Array.isArray(data) && data.length > 0) {
                    const defaultCountry = data[0];
                    setSelectedCountry(defaultCountry);
                    try {
                        const regs = await getRegions(defaultCountry.id);
                        setRegions(regs || []);
                        const countryName = getLocalizedName(defaultCountry, i18n.language);
                        updateVipLocationStorage(defaultCountry.id, null, countryName, "");
                    } catch (e) { console.error("Failed to load regions:", e); }
                }
            } catch (e) { console.error("Failed to load countries:", e); }
        };
        loadCountries();
    }, [i18n.language]);

    const updateVipLocationStorage = (countryId, regionId, countryName, regionName) => {
        const loc = { country_id: countryId || null, region_id: regionId || null, district_id: null, country: countryName || "", province: regionName || "", locality: "", area: "" };
        localStorage.setItem("vipSelectedLocation", JSON.stringify(loc));
        window.dispatchEvent(new Event("agroSelectedLocationChanged"));
    };

    const handleCountrySelect = async (option) => {
        const country = countries.find((c) => getLocalizedName(c, i18n.language) === option) || null;
        setSelectedCountry(country);
        setSelectedRegion(null);
        setRegions([]);
        updateVipLocationStorage(country?.id ?? null, null, option, "");
        if (country) {
            try {
                const regs = await getRegions(country.id);
                setRegions(regs || []);
            } catch (e) { console.error("Failed to load regions:", e); }
        }
    };

    const handleRegionSelect = (option) => {
        const region = regions.find((r) => getLocalizedName(r, i18n.language) === option) || null;
        setSelectedRegion(region);
        const countryName = selectedCountry ? getLocalizedName(selectedCountry, i18n.language) : "";
        updateVipLocationStorage(selectedCountry?.id ?? null, region?.id ?? null, countryName, option);
    };

    const handleSearch = () => {
        resetAndRefetch();
    };

    const handleResetFilters = () => {
        setCategoryFilter({ categoryId: null, subcategoryId: null });
        setSelectedCategoryObj(null);
        setSelectedSubcategoryObj(null);
        setActiveRubricId(null);
        setSortOption('');
        setNegotiableOnly(false);
        setMinPrice('');
        setMaxPrice('');
        // don't reset advertMode — tabs stay as user chose
    };

    const hasMore = hasMoreVip || hasMoreNonVip;

    return (
        <div className="flex gap-6 mt-[50px] px-5 md:px-[60px] lg:px-[120px] xl:px-[240px] pb-[60px] w-full items-start">

            {/* Left: Filter panel */}
            <div className="hidden md:flex flex-col w-[220px] flex-shrink-0">
                <div className="bg-white rounded-2xl p-5 sticky top-[90px]">
                    {/* Mode tabs */}
                    <div className="flex bg-gray-100 rounded-xl p-1 mb-4">
                        <button
                            onClick={() => setAdvertMode('regular')}
                            className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all ${advertMode === 'regular' ? 'bg-[#147F26] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {t('regularAds') || 'Жарнамалар'}
                        </button>
                        <button
                            onClick={() => setAdvertMode('dealer')}
                            className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all ${advertMode === 'dealer' ? 'bg-[#147F26] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {t('dealerAds') || 'Дилерлік'}
                        </button>
                    </div>

                    {/* Category */}
                    <FilterRow label={t('selectCategory') || 'Категория'} defaultOpen={true}>
                        <div className="pt-1 flex flex-col gap-2">
                            <Dropdown
                                text={t('selectCategory') || 'Категория'}
                                options={allCategories.map(c => getCategoryName(c))}
                                selected={selectedCategoryObj ? getCategoryName(selectedCategoryObj) : undefined}
                                width="full"
                                onOptionSelect={handleCategoryDropdownSelect}
                            />
                            {selectedCategoryObj && (selectedCategoryObj.subcategories || []).length > 0 && (
                                <Dropdown
                                    text={t('selectSubcategory') || 'Субкатегория'}
                                    options={(selectedCategoryObj.subcategories || []).map(s => getSubcategoryName(s))}
                                    selected={selectedSubcategoryObj ? getSubcategoryName(selectedSubcategoryObj) : undefined}
                                    width="full"
                                    onOptionSelect={handleSubcategoryDropdownSelect}
                                />
                            )}
                        </div>
                    </FilterRow>

                    {/* Region */}
                    {regions.length > 0 && (
                        <FilterRow label={t('region')}>
                            <div className="pt-1">
                                <Dropdown
                                    text={t('region')}
                                    options={regions.map((r) => getLocalizedName(r, i18n.language))}
                                    selected={selectedRegion ? getLocalizedName(selectedRegion, i18n.language) : undefined}
                                    width="full"
                                    onOptionSelect={handleRegionSelect}
                                />
                            </div>
                        </FilterRow>
                    )}

                    {/* Sort by date */}
                    <FilterRow label={t('sortByDate') || 'Күні бойынша'}>
                        <div className="pt-1">
                            <Dropdown
                                text={t('sortByDate') || 'Күні бойынша'}
                                options={[t('dateDesSort'), t('dateAscSort')]}
                                width="full"
                                onOptionSelect={handleDateSortChange}
                            />
                        </div>
                    </FilterRow>

                    {/* Sort by price */}
                    <FilterRow label={t('sortByPrice') || 'Баға бойынша'}>
                        <div className="pt-1">
                            <Dropdown
                                text={t('sortByPrice') || 'Баға бойынша'}
                                options={[t('priceAscSort'), t('priceDesSort')]}
                                width="full"
                                onOptionSelect={handlePriceSortChange}
                            />
                        </div>
                    </FilterRow>

                    {/* Price range */}
                    <FilterRow label={t('price')} defaultOpen={true}>
                        <div className="flex gap-2 pt-1">
                            <input
                                type="number"
                                placeholder={t('from') || 'от'}
                                value={minPrice}
                                onChange={(e) => setMinPrice(e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#147F26]"
                            />
                            <input
                                type="number"
                                placeholder={t('to') || 'до'}
                                value={maxPrice}
                                onChange={(e) => setMaxPrice(e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#147F26]"
                            />
                        </div>
                        {minPrice || maxPrice ? (
                            <p className="text-[10px] text-gray-400 mt-1">{t('тт/т') || 'тт/т'}</p>
                        ) : null}
                    </FilterRow>

                    {/* Negotiable toggle */}
                    <div className="flex items-center justify-between py-3 border-t border-gray-100 mt-2">
                        <span className="text-sm text-gray-700">{t('negotiable') || 'Келіссөз'}</span>
                        <button
                            onClick={() => setNegotiableOnly(!negotiableOnly)}
                            className={`w-10 h-5 rounded-full transition-colors relative ${negotiableOnly ? 'bg-[#147F26]' : 'bg-gray-200'}`}
                        >
                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${negotiableOnly ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </button>
                    </div>

                    {/* Action buttons */}
                    <div className="flex flex-col gap-2 mt-3">
                        <button
                            onClick={handleSearch}
                            className="w-full py-2.5 bg-[#147F26] hover:bg-[#0f5f1a] text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            {t('search') || 'Іздеу'}
                        </button>
                        <button
                            onClick={handleResetFilters}
                            className="w-full py-2.5 border border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-700 text-sm font-medium rounded-xl transition-colors"
                        >
                            {t('reset') || 'Тазарту'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Right: Content */}
            <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-gray-900 mb-4 m-0">{t("featuredAds")}</h3>

                {/* Rubrics row */}
                {rubrics.length > 0 && (
                    <div className="mb-5">
                        <div className="flex items-start gap-2">
                            <div
                                className={`flex-1 min-w-0 ${showAllRubrics ? "flex flex-wrap gap-2" : "flex gap-2 overflow-x-auto scrollbar-none"} pb-2`}
                                style={showAllRubrics ? {} : { scrollbarWidth: 'none' }}
                            >
                                {rubrics.map((rubric) => (
                                    <RubricItem
                                        key={rubric.rubric_id}
                                        imageUrl={rubric.image_url || rubric.categories[0]?.image_url || null}
                                        displayName={getRubricName(rubric)}
                                        isActive={activeRubricId === rubric.rubric_id}
                                        onClick={() => {
                                            const newId = activeRubricId === rubric.rubric_id ? null : rubric.rubric_id;
                                            setActiveRubricId(newId);
                                            if (newId) {
                                                const firstCat = rubric.categories[0] || null;
                                                setSelectedCategoryObj(firstCat);
                                                setSelectedSubcategoryObj(null);
                                                setCategoryFilter({ categoryId: firstCat?.category_id || null, subcategoryId: null });
                                            } else {
                                                setSelectedCategoryObj(null);
                                                setSelectedSubcategoryObj(null);
                                                setCategoryFilter({ categoryId: null, subcategoryId: null });
                                            }
                                        }}
                                    />
                                ))}
                            </div>
                            <button
                                onClick={() => setShowAllRubrics(!showAllRubrics)}
                                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 hover:border-[#147F26]/40 hover:bg-[#147F26]/5 text-gray-500 hover:text-[#147F26] transition-all bg-white"
                            >
                                <svg className={`w-4 h-4 transition-transform ${showAllRubrics ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h8" />
                                </svg>
                                <span className="text-sm font-medium whitespace-nowrap">
                                    {showAllRubrics ? (t('collapse') || 'Жию') : (t('showAll') || 'Барлығы')}
                                </span>
                            </button>
                        </div>
                    </div>
                )}

                {isLoading ? (
                    <Loading />
                ) : displayedAdverts.length === 0 ? (
                    /* Empty state */
                    <div className="flex flex-col items-center justify-center py-16 gap-4">
                        <div className="w-44 h-32 rounded-2xl overflow-hidden flex items-center justify-center bg-white border border-gray-100">
                            <img src={siteLogo} alt="logo" className="w-full h-full object-contain p-4" />
                        </div>
                        <div className="bg-gray-100 rounded-2xl px-8 py-5 text-center max-w-xs">
                            <p className="text-sm font-semibold text-gray-700 mb-1">{t('filters')}</p>
                            <p className="text-xs text-gray-500">{t('selectFilterToSeeProducts') || 'Выберите параметры фильтра, чтобы увидеть товары.'}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full">
                            {displayedAdverts.map((advert) => (
                                <AdvertItem
                                    key={advert._key}
                                    id={advert.id}
                                    imagePath={advert.main_image_url || image1}
                                    name={advert.title}
                                    price={advert.price}
                                    price_type={advert.price_type}
                                    is_negotiable={advert.is_negotiable}
                                    location={advert.location}
                                    date={advert.created_at}
                                    barter={advert.barter}
                                    is_favorite={advert.is_favorite}
                                    promotion_package={advert.promotion_package}
                                    measurement_unit={advert.measurement_unit}
                                />
                            ))}
                        </div>
                        {hasMore && <div ref={observerTarget} className="h-5 w-full" />}
                    </>
                )}

                {isLoadingMore && (
                    <div className="text-center py-4"><Loading /></div>
                )}
            </div>
        </div>
    );
}

export default AdvertSection;
